import { useQuery } from '@apollo/client'
import { Box, Html } from '@react-three/drei'
import { captureEvent } from '@sentry/react'
import { Fragment, PropsWithChildren, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box3, Group, Matrix4, Vector3 } from 'three'
import { useProductDistanceEngine } from '~/components/DrawingCanvas/lib/productDistanceEngine'
import { LINE_COLOR, ARROW_SIZE } from '~/components/DrawingCanvas/Products/constants'
import { useCardinalArrowConfigs } from '~/components/DrawingCanvas/Products/hooks/useCardinalArrowConfigs'
import { useGetFanHeightInches } from '~/components/DrawingCanvas/Products/hooks/useGetFanHeightInches'
import { getArrowDimensions } from '~/components/DrawingCanvas/Products/util'
import {
  modelToUI,
  scaleModelVectorToUI,
  scaleUIVectorToModel,
  vectorModelToUI,
  vectorUIToModel,
} from '~/components/DrawingCanvas/util/units'
import theme from '~/config/theme'
import { graphql } from '~/gql'
import { useDistanceDimensions } from '~/hooks/useDistanceDimensions'
import { useSelectedProducts } from '~/hooks/useSelectedProducts'
import { useAppDispatch, useAppSelector, useAppStore } from '~/store/hooks'
import { updateObjects, updateObjectsPassively, updateProducts } from '~/store/objects'
import { Obstruction, Product } from '~/store/objects/types'
import { clearStatus, setStatus } from '~/store/status'
import { inputStyles } from '~/ui/Field'
import { R3FDimensionInput } from '~/ui/R3FDimensionInput'
import { DragControls, DragControlsProps } from '../../util/DragControls'
import LayerKeys from '~/config/layerKeys'
import { useSelectedObstructions } from '~/hooks/useSelectedObstructions'

declare global {
  interface WindowEventMap {
    multidrag: CustomEvent<{ delta: Vector3; totalDelta: Vector3 }>
  }
}

const SELECTION_BOX_COLOR = theme.colors.three.selectionBox

const isOverhead = (product: Product) => {
  return product.isDirectionalOverhead || product.layerKey === 'PRODUCTS_OVERHEAD'
}

const updateObstructions = (obstructions: Obstruction[], delta: Vector3) => {
  return obstructions.map(obstruction => {
    const newPosition = new Vector3().copy(obstruction.position).add(delta)
    const newPositions = obstruction.positions.map(p => ({ x: p.x + delta.x, y: p.y + delta.y, z: p.z }))
    return {
      ...obstruction,
      position: newPosition,
      positions: newPositions,
    }
  })
}

const useAdjustHeight = () => {
  const products = useAppSelector(state => state.objects.present.products)
  const overheadProducts = Object.values(products).filter(isOverhead)
  const overheadVariationIds = Array.from(new Set(overheadProducts.map(p => p.variationId)))
  const isSelectedSomeOverhead = overheadVariationIds.length > 0
  const getFanHeight = useGetFanHeightInches()
  const { data } = useQuery(
    graphql(`
      query MultiDragFullHeights($variationIds: [ID!]!) {
        ProductVariations(ids: $variationIds) {
          id
          voltages {
            id
            mountingOptions {
              id
              fullHeight
            }
          }
        }
      }
    `),
    {
      variables: { variationIds: overheadVariationIds },
      skip: !isSelectedSomeOverhead,
      onError: error => captureEvent(error),
    }
  )
  return useCallback(
    (product: Product, newPosition: Vector3) => {
      if (!data || !isOverhead(product)) return
      const voltage = new Map(data.ProductVariations.map(({ id, voltages }) => [id, voltages]))
      const { variationId, voltageId, mountingOptionId } = product
      const mountingOptions = voltage.get(variationId)?.find(v => v.id === voltageId)?.mountingOptions
      const fullHeight = mountingOptions?.find(m => m.id === mountingOptionId)?.fullHeight
      if (!fullHeight) return
      const newHeight = getFanHeight({ origin: newPosition, fullHeight })
      newPosition.setZ(newHeight)
    },
    [getFanHeight, data]
  )
}

export const MultiProductDragControls = ({ children }: PropsWithChildren) => {
  const productDistanceEngine = useProductDistanceEngine()
  const dispatch = useAppDispatch()
  const selectedProducts = useSelectedProducts()
  const selectedObstructions = useSelectedObstructions()
  const { locked: isLockedProducts, visible: isVisibleProducts } = useAppSelector(
    ({ layers }) => layers.layers.PRODUCTS
  )
  const { locked: isLockedObstructions, visible: isVisibleObstructions } = useAppSelector(
    ({ layers }) => layers.layers.OBSTRUCTIONS
  )
  const isMultipleSelected = selectedProducts.length + selectedObstructions.length > 1
  const isDragDisabled =
    !isMultipleSelected ||
    ((isLockedProducts || !isVisibleProducts) && selectedProducts.length > 0) ||
    ((isLockedObstructions || !isVisibleObstructions) && selectedObstructions.length > 0)

  const dragGroup = useRef<Group>(null!)
  const deltaAccumulator = useRef(new Vector3())
  const dragStartMatrix = useRef(new Matrix4())
  const [isDragging, setIsDragging] = useState(false)
  const { displayFormattedValue } = useDistanceDimensions()
  const arrowConfigs = useCardinalArrowConfigs()
  const midpoint = new Vector3()
  const arrowOrigin = new Vector3()

  const store = useAppStore()
  const products = useAppSelector(state => state.objects.present.products)
  const obstructions = useAppSelector(state => state.objects.present.obstructions)
  const ignoring = new Set(Object.keys(products).concat(Object.keys(obstructions)))
  const adjustHeight = useAdjustHeight()
  const selectedProductsMeshGroup = selectedProducts.reduce((group, product) => {
    const mesh = productDistanceEngine.productMeshes.get(product.id)
    if (mesh) group.add(mesh.clone())
    return group
  }, new Group())
  const selectedObstructionsMeshGroup = selectedObstructions.reduce((group, obstruction) => {
    const obstructionMesh = productDistanceEngine.obstructionMeshes.get(obstruction.id)
    if (obstructionMesh) group.add(obstructionMesh.clone())
    return group
  }, new Group())
  selectedProductsMeshGroup.add(selectedObstructionsMeshGroup)

  const box = new Box3().setFromObject(selectedProductsMeshGroup)
  const position = box.getCenter(new Vector3())
  const size = box.getSize(new Vector3())
  box.setFromCenterAndSize(new Vector3(), vectorModelToUI(size))

  useEffect(
    () =>
      productDistanceEngine.subscribeSelector(engine => engine.roofMeshes)(() => {
        const products = store.getState().objects.present.products
        const updatedProducts = Object.values(products).map(product => {
          const newPosition = new Vector3().copy(product.position)
          adjustHeight(product, newPosition)
          return {
            ...product,
            position: { x: newPosition.x, y: newPosition.y, z: newPosition.z },
          }
        })

        dispatch(updateObjectsPassively(updatedProducts))
        dispatch(clearStatus())
      }),
    [productDistanceEngine, adjustHeight]
  )

  useLayoutEffect(() => {
    dragGroup.current.position.copy(vectorModelToUI(position))
    dragGroup.current.updateMatrix()
  }, [position.x, position.y, position.z])

  const handleDragStart = () => {
    setIsDragging(true)
    dragStartMatrix.current.copy(dragGroup.current.matrix)
  }

  const handleDrag: DragControlsProps['onDrag'] = (_, totalDelta, eventDelta) => {
    dragGroup.current.position.add(eventDelta)
    dragGroup.current.updateMatrix()

    deltaAccumulator.current.add(eventDelta)
    window.dispatchEvent(
      new CustomEvent<{ delta: Vector3; totalDelta: Vector3 }>('multidrag', {
        detail: { delta: eventDelta, totalDelta },
      })
    )
    arrowConfigs.forEach(({ arrowHelper, direction, htmlMesh, div }) => {
      arrowOrigin.copy(dragGroup.current.position)
      scaleUIVectorToModel(arrowOrigin)
      const { length: distance, offset } = getArrowDimensions({
        position: arrowOrigin,
        direction,
        boundingBoxSize: size,
        ignoring,
      })
      if (!distance) return
      const arrowLength = modelToUI(distance)
      arrowHelper.current?.setLength(arrowLength, ARROW_SIZE, ARROW_SIZE)
      scaleModelVectorToUI(offset)
      midpoint.copy(direction).multiplyScalar(arrowLength / 2 + Math.abs(offset.x + offset.y))
      htmlMesh.current?.position.copy(midpoint)
      if (div.current) div.current.textContent = displayFormattedValue(distance)
    })
  }

  const handleDragEnd = () => {
    setIsDragging(false)
    const delta = vectorUIToModel(deltaAccumulator.current)
    const updatedProducts = selectedProducts.map(product => {
      const { x, y, z } = product.position
      const newPosition = new Vector3(x, y, z).add(delta)
      adjustHeight(product, newPosition)
      return {
        ...product,
        position: { x: newPosition.x, y: newPosition.y, z: newPosition.z },
      }
    })
    const isValid = updatedProducts.every(({ position }) => productDistanceEngine.isWithinFacility(position))
    if (!isValid) {
      dragGroup.current.position.setFromMatrixPosition(dragStartMatrix.current)
      const deltaReset = deltaAccumulator.current.negate()
      window.dispatchEvent(
        new CustomEvent<{ delta: Vector3; totalDelta: Vector3 }>('multidrag', {
          detail: { delta: deltaReset, totalDelta: new Vector3() },
        })
      )
      dispatch(setStatus({ type: 'error', text: 'Product must be placed within facility bounds!' }))
    } else {
      dispatch(updateObjects(updateObstructions(selectedObstructions, delta)))
      dispatch(updateProducts(updatedProducts))
    }
    deltaAccumulator.current.set(0, 0, 0)
  }

  const handleCommit =
    ({ distance, direction }: { distance: number; direction: Vector3 }) =>
    (newValue: number) => {
      const distanceDelta = distance - newValue
      const scaledDirection = new Vector3().copy(direction).multiplyScalar(distanceDelta)
      const updatedProducts = selectedProducts.map(product => {
        const { x, y, z } = product.position
        const newPosition = new Vector3(x, y, z).add(scaledDirection)
        adjustHeight(product, newPosition)
        return { ...product, position: { x: newPosition.x, y: newPosition.y, z: newPosition.z } }
      })
      dispatch(updateObjects(updateObstructions(selectedObstructions, scaledDirection)))
      dispatch(updateProducts(updatedProducts))
    }

  return (
    <>
      <DragControls
        name={
          isMultipleSelected
            ? isLockedProducts || !isVisibleProducts
              ? LayerKeys.OBSTRUCTIONS
              : LayerKeys.PRODUCTS
            : undefined
        }
        ref={dragGroup}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        dragDisabled={isDragDisabled}
      >
        <group visible={isMultipleSelected}>
          {arrowConfigs.map(({ arrowHelper, direction, div, htmlMesh }, i) => {
            const { length: distance, offset } = getArrowDimensions({
              position,
              direction,
              boundingBoxSize: size,
              ignoring,
            })
            if (!distance) return null
            const origin = vectorModelToUI(offset)
            const htmlPosition = new Vector3().copy(direction).multiplyScalar(distance / 2)
            htmlPosition.add(offset)
            scaleModelVectorToUI(htmlPosition)
            return (
              <Fragment key={i}>
                <mesh ref={htmlMesh} position={htmlPosition}>
                  {isDragging ? (
                    <Html center ref={div} className={inputStyles({ isOnCanvas: true })}></Html>
                  ) : (
                    isMultipleSelected && (
                      <R3FDimensionInput
                        center
                        aria-label={`Product distance guideline ${i}`}
                        value={distance}
                        onCommit={handleCommit({ distance, direction })}
                      />
                    )
                  )}
                </mesh>
                <arrowHelper
                  ref={arrowHelper}
                  args={[direction, origin, modelToUI(distance), LINE_COLOR, ARROW_SIZE, ARROW_SIZE]}
                />
              </Fragment>
            )
          })}
          <box3Helper args={[box, SELECTION_BOX_COLOR]} />
          <Box args={[modelToUI(size.x), modelToUI(size.y), modelToUI(size.z)]}>
            <meshStandardMaterial color={SELECTION_BOX_COLOR} transparent opacity={0.25} depthWrite={false} />
          </Box>
        </group>
      </DragControls>
      {children}
    </>
  )
}
